import React, { Fragment, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import {
  setFormValue,
  setCurrentStepIndex,
  setSteps,
  resetFileInfo,
  resetDatabaseInfo,
  resetDatabaseTableInfo,
  setPageValue,
  resetApiInfo,
  getTablesFromDbConnection,
  getNumberOfRowsFromFile,
  importDataFromFile,
  importDataByInvokingApiEndpoint,
  importDataFromDB,
  getDatasetSchemeFromDBConnection,
  getSampleDataFromApiEndpoint,
  setProcessing,
} from "../../../redux/slices/importdatasetpageslice";
import { Form, FormElement } from "@progress/kendo-react-form";
import { formValidator } from "./importdatasetpageformvalidator";
import { Stepper } from "@progress/kendo-react-layout";
import { addNotification } from "../../../redux/slices/notificationsslice";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const ImportDatasetPageForm = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.importDatasetPage);
  const appearance = useSelector((state) => state.appearance);
  const importDatasetFrom = pageState.formValues.ImportFrom;
  const updateDirtyState = props.updateDirtyState;
  const currentStepIndex = pageState.currentStepIndex;

  const steps = pageState.steps;
  const stepPages = pageState.stepPages;
  const stepperOrientation = pageState.stepperOrientation;

  const lastStepIndex = steps.length - 1;
  const isLastStep = lastStepIndex === currentStepIndex;
  const isResultStep = lastStepIndex - 1;

  let headers = pageState.formValues.Headers;
  const latestFormValuesRef = useRef(pageState.formValues);
  let filenameXD = latestFormValuesRef.current.Filename;
  let fileIdXD = latestFormValuesRef.current.FileId;

  const { t } = useTranslation();

  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  useEffect(() => {
    latestFormValuesRef.current = pageState.formValues;
  }, [pageState.formValues]);

  useEffect(() => {
    if (filenameXD && fileIdXD) {
      validateFileForm(latestFormValuesRef.current, false); // Pass the latest values without triggering re-renders
    }
  }, [filenameXD, fileIdXD]);

  useEffect(() => {
    headers = pageState.formValues.Headers;
  }, [pageState.formValues.Headers]);

  const validateSelection = async (values) => {
    console.log("validate selection");
    values.File = null;
    dispatch(setPageValue({ id: "sheets", value: [] }));
    dispatch(resetDatabaseInfo());
    dispatch(resetFileInfo());

    if (values.SelectedDataset == "") {
      dispatch(
        addNotification({
          text: t("datasets.importdataset.selectdataset"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }
    return true;
  };

  const notify = (text) => {
    dispatch(
      addNotification({
        text,
        type: "error",
        closable: true,
        duration: 5,
        position: "topRight",
      })
    );
  };

  const validateFileForm = async (values, render = true) => {
    console.log("validateFileForm");
    const { SelectedSheet, StartingRow, EndingRow } = values;
    if (!render) return;
    if (
      !latestFormValuesRef.current.Filename ||
      !latestFormValuesRef.current.FileId
    )
      return notify(t("datasets.importdataset.selectfile1")), false;
    if (!SelectedSheet)
      return notify(t("datasets.importdataset.selectsheet")), false;
    if (EndingRow && +StartingRow > +EndingRow) {
      notify(t("datasets.importdataset.row"));
      return false;
    }

    try {
      await dispatch(
        getNumberOfRowsFromFile(
          latestFormValuesRef.current.FileId,
          SelectedSheet,
          true,
          true,
          StartingRow,
          EndingRow,
          pageState.formValues.SelectedDataset,
          t
        )
      );
      return true;
    } catch (error) {
      console.log("Error creating dataset: ", error);
      return false;
    }
  };

  const validateDatabaseForm = async (values) => {
    const { DatabaseType, Server, Database, Username, Password } = values;
    if (![DatabaseType, Server, Database, Username, Password].every(Boolean)) {
      notify(t("datasets.importdataset.fillfields"));
      return false;
    }
    try {
      await dispatch(getTablesFromDbConnection(t));
      return true;
    } catch (error) {
      console.log("Error creating dataset: ", error);
      return false;
    }
  };

  const validateTableForm = async (values) => {
    const table = values.Table;
    const query = values.Query;
    const dbConnection = {
      DataBaseTypeId: values.DatabaseTypeId,
      Server: values.Server,
      DataBase: values.Database,
      Username: values.Username,
      Password: values.Password,
    };
    try {
      if (
        (table === 0 || table === "" || table === undefined) &&
        (query == "" || query == undefined)
      ) {
        notify(t("datasets.importdataset.selecttable"));

        return false;
      }
      await dispatch(
        getDatasetSchemeFromDBConnection(table, query, dbConnection, t)
      );
      return true;
    } catch (error) {
      // Handle error (show a notification, etc.)
      notify(t("datasets.importdataset.importerror"));
      return false; // Failure
    }
  };

  const validateApiForm = async (values) => {
    const url = values.Url;
    const method = values.Method;
    const headers = values.Headers;
    const datasetId = values.SelectedDataset;
    let paramHd = headers;
    try {
      if (url === "" || method === "") {
        notify(t("datasets.importdataset.fillerror"));
        return false;
      }

      const headerKeys = new Set();
      for (const header of headers) {
        if (headerKeys.has(header.key)) {
          notify(t("datasets.importdataset.duplicate_error"));
          return false;
        }
        headerKeys.add(header.key);
      }
      if (
        headers.length === 1 &&
        headers[0].key === "" &&
        headers[0].value === ""
      ) {
        paramHd = [];
      }

      await dispatch(
        getSampleDataFromApiEndpoint(datasetId, url, method, paramHd, t)
      );
      return true;
    } catch (error) {
      // Handle error (show a notification, etc.)
      notify(t("datasets.importdataset.importerror"));
      return false; // Failure
    }
  };

  const scrollToTop = () => {
    const container = document.getElementById("error-list");
    if (container) {
      container.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const validateReview = async (values) => {
    try {
      if (pageState.formValues.ImportFrom === "fromfile") {
        return await dispatch(
          importDataFromFile(
            pageState.formValues.SelectedDataset,
            pageState.formValues.FileId,
            true,
            values.StartingRow,
            values.EndingRow,
            values.SelectedSheet,
            t
          )
        ).then(
          (response) => {
            if (response.data.Succeeded) {
              return true;
            } else {
              scrollToTop();
              return false;
            }
          },
          (err) => {
            dispatch(setProcessing({ processing: false }));
            dispatch(setPageValue({ id: "error_lst", value: err.data.Errors }));
            scrollToTop();
            return false;
          }
        );
      } else if (pageState.formValues.ImportFrom === "fromdb") {
        let table = values.Table;
        let query = values.Query;
        if (values.ChooseFrom === "table") query = "";
        else table = "";

        const dbConnection = {
          DataBaseTypeId: values.DatabaseTypeId,
          Server: values.Server,
          DataBase: values.Database,
          Username: values.Username,
          Password: values.Password,
        };
        return await dispatch(
          importDataFromDB(
            pageState.formValues.SelectedDataset,
            null,
            table,
            query,
            dbConnection,
            t
          )
        ).then(
          (response) => {
            if (response.data.Succeeded) {
              return true;
            } else {
              scrollToTop();
              return false;
            }
          },
          (err) => {
            dispatch(setProcessing({ processing: false }));
            dispatch(setPageValue({ id: "error_lst", value: err.data.Errors }));
            scrollToTop();
            return false;
          }
        );
      } else if (pageState.formValues.ImportFrom === "fromapi") {
        const headers = values.Headers;
        let paramHd = headers;
        if (
          headers.length === 1 &&
          headers[0].key === "" &&
          headers[0].value === ""
        ) {
          paramHd = [];
        }
        return await dispatch(
          importDataByInvokingApiEndpoint(
            pageState.formValues.SelectedDataset,
            values.Url,
            values.Method,
            values.ReccursEvery,
            paramHd,
            t
          )
        ).then(
          (response) => {
            if (response.data.Succeeded) {
              return true;
            } else {
              scrollToTop();
              return false;
            }
          },
          (err) => {
            dispatch(setProcessing({ processing: false }));
            dispatch(setPageValue({ id: "error_lst", value: err.data.Errors }));
            scrollToTop();
            return false;
          }
        );
      }
      return true; // Success
    } catch (error) {
      // Handle error (show a notification, etc.)
      notify(t("datasets.importdataset.importerror"));
      return false; // Failure
    }
  };

  const checkStepValidity = async (values) => {
    let stepIsValid = true;
    if (currentStepIndex === 0) {
      stepIsValid = await validateSelection(values);
    } else if (importDatasetFrom == "fromfile") {
      if (currentStepIndex === 1) {
        stepIsValid = await validateFileForm(values);
      } else if (currentStepIndex === 2) {
        stepIsValid = await validateReview(values);
      }
    } else if (importDatasetFrom == "fromdb") {
      if (currentStepIndex === 1) {
        stepIsValid = await validateDatabaseForm(values);
      } else if (currentStepIndex === 2) {
        stepIsValid = await validateTableForm(values);
      } else if (currentStepIndex === 3) {
        stepIsValid = await validateReview(values);
      }
    } else if (importDatasetFrom == "fromapi") {
      if (currentStepIndex === 1) {
        stepIsValid = await validateApiForm(values);
      } else if (currentStepIndex === 2) {
        stepIsValid = await validateReview(values);
      }
    }
    return stepIsValid;
  };

  const onStepSubmit = React.useCallback(
    async (event) => {
      const { values } = event;
      // Validate the current step
      const stepIsValid = await checkStepValidity(values);

      // Update steps' validity
      const updatedSteps = steps.map((step, index) => ({
        ...step,
        isValid: index === currentStepIndex ? stepIsValid : step.isValid,
      }));
      dispatch(setSteps(updatedSteps));

      // If the step is valid, move to the next step, otherwise stay on the current step
      if (stepIsValid) {
        dispatch(setFormValue(values)); // Persist updated form values to Redux

        if (currentStepIndex < lastStepIndex) {
          dispatch(setCurrentStepIndex(currentStepIndex + 1));
        }
      }
    },
    [steps, currentStepIndex, lastStepIndex, isLastStep]
  );

  const onPrevClick = React.useCallback(
    (event) => {
      event.preventDefault();
      // Reset state based on step and import source
      if (currentStepIndex === 1 && importDatasetFrom === "fromfile") {
        dispatch(resetFileInfo());
      } else if (currentStepIndex === 1 && importDatasetFrom === "fromdb") {
        dispatch(resetDatabaseInfo());
      } else if (currentStepIndex === 2 && importDatasetFrom === "fromdb") {
        dispatch(resetDatabaseTableInfo());
      } else if (currentStepIndex === 1 && importDatasetFrom === "fromapi") {
        dispatch(resetApiInfo());
      }
      dispatch(setCurrentStepIndex(Math.max(currentStepIndex - 1, 0)));
    },
    [currentStepIndex, importDatasetFrom]
  );

  return (
    <Fragment>
      <div className="form-wrapper">
        <div className={"stepper-wrapper"}>
          <Stepper
            value={currentStepIndex}
            items={steps}
            orientation={stepperOrientation}
          />
        </div>
        <Form
          validator={formValidator}
          onSubmitClick={onStepSubmit}
          initialValues={pageState.formInitialValues}
          values={pageState.formValues}
          render={(formRenderProps) => {
            const StepComponent = stepPages[currentStepIndex];
            return (
              <FormElement horizontal={false}>
                <StepComponent
                  formValues={pageState.formValues}
                  dispatch={dispatch}
                />
                {/* Navigation Buttons */}

                <div className="navigation-buttons">
                  <div style={{ marginTop: "30px" }}>
                    {!isLastStep && (
                      <Button
                        className="btn-proceed"
                        //disabled={!formRenderProps.valid}
                        onClick={formRenderProps.onSubmit}
                      >
                        {isResultStep &&
                        (pageState.formValues.ImportFrom == "fromdb" ||
                          pageState.formValues.ImportFrom == "fromfile" ||
                          pageState.formValues.ImportFrom == "fromapi")
                          ? t("datasets.importdataset.next")
                          : t("datasets.importdataset.import2")}
                      </Button>
                    )}

                    {currentStepIndex !== 0 && !isLastStep ? (
                      <Button className="btn-cancel" onClick={onPrevClick}>
                        {t("datasets.importdataset.previous")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </FormElement>
            );
          }}
        />
      </div>
      <style>
        {`        
                    .datasets-page-form .form-wrapper{
                        width:100%;
                    }
                    .datasets-page-form .form-wrapper .stepper-wrapper{
                      padding:20px;
                        width:100%;
                        border: ${currentTheme.managerBorder};
                        background-color: ${currentTheme.componentBgFaded};
                        border-left:0;
                    }
                    .datasets-page-form .form-wrapper .k-stepper{
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }
                    .datasets-page-form .form-wrapper .k-stepper .k-step-list{
                        height:100%;
                    }
                    .datasets-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        flex-direction:column;
                       
                        border: ${currentTheme.managerBorder};
                        padding:15px;
                        overflow-x:auto;

                    }

                    .datasets-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .datasets-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }

                   
                    .datasets-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .datasets-page-form .form-region-root
                    {
                        background-color: transparent;
                    }
                    
                   
                    .datasets-page-form .form-group-1 .toggle-container
                    {
                        /*the div containing the 3 checks*/
                        justify-content: space-between;
                        border-color: ${currentTheme.componentBorderColor};
                        width: 100%;/*620px;*/
                        max-width: 1000px;
                    }

                    .datasets-page-form input.k-input, .datasets-page-form textarea.k-textarea, 
                    .datasets-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    } 

                    .snippet-popup .k-menu-item:nth-child(2)
                    {
                        padding: 0 !important;
                    }

                    .snippet-popup .k-menu-item:nth-child(1),
                    .snippet-popup .k-menu-item:nth-child(3)
                    {
                        padding-top: 3px;
                        padding-bottom: 3px;
                    }


                    .datasets-page-form .k-form-field textarea
                    {
                        height: 200px !important;
                    }

                     .datasets-page-form .k-form-field .k-radio-list{
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                     
                     }

                    .datasets-page-form .k-form .k-dropdownlist{
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }

                    .k-animation-container-shown{
                        direction: ${
                          appearance.isRightToLeft ? "rtl" : "ltr"
                        } !important;
                    }

                    .datasets-page-form .navigation-buttons{
                      display: flex;
                      justify-content: space-between;
                      align-content: center;
                      direction: rtl;
                      margin-top:50px;
                    }
                    .datasets-page-form .navigation-buttons .btn-proceed{
                      padding: 6px 28px;
                      border-radius: 0px;
                      border-color: ${currentTheme.primary};
                      color: #fff;
                      min-width:120px;
                      background-color:  ${currentTheme.primary};
                    }
                    .datasets-page-form .navigation-buttons .btn-cancel{
                      margin-right: 16px;
                      padding: 6px 28px;
                      min-width:120px;
                      border-radius: 0px;
                      border-color: rgba(0, 0, 0, 0.1);
                      color: #424242;
                      background-color: #f5f5f5;
                    }
                `}
      </style>
    </Fragment>
  );
};
